import { createCharacter } from './builder';
import { createAttackAnimation, createIdleAnimation, createJumpAnimation, createRunAnimation } from './animations';
import { createChanneledAttack, createRangedProjectileAttack } from './attacks';
import { RUN_SPEED, UPPER_BODY_MASK } from './constants';
import { createBasicLifecycleScript } from '../lifecycle';
import { 
  createParticleStreamPreset,
  createBeamDamagePreset,
  createChannelEffectsPreset,
  createBeamWithProjectilesPreset,
  createMuzzleLightPreset,
  combinePresets,
} from '../combat/channelPresets';
import { Color3, Vector3 } from '@babylonjs/core';
import { fromFXPreset } from '../../systems/effects/FXPresets';

export const LILLY_CONFIG = createCharacter({
  id: 'lilly',
  name: 'Lilly',
  assetPath: '/assets/characters/lilly/lilly2.low/Happy Idle-reexport.glb',
  scale: 1.0,
  modelAxes: {
    forward: { x: 0, y: 0, z: -1 },
    up: { x: 0, y: 1, z: 0 }
  },
  moveSpeed: RUN_SPEED * 1.2,  // 10.0 - double speed
  physics: {
    jumpHeight: 1.8
  },
  collidesWithCharacters: false,
  turnMode: 'interpolate',
  animations: {
    idle: createIdleAnimation('/assets/characters/lilly/lilly2.low/Happy Idle.glb'),
    run: createRunAnimation('/assets/characters/lilly/lilly2.low/Running.glb', {
      speed: 1
    }),
    jump: createJumpAnimation('/assets/characters/lilly/lilly2.low/Running Jump.glb'),
    fall: createIdleAnimation('/assets/characters/lilly/lilly2.low/Falling Idle.glb'),
    turn: createRunAnimation('/assets/characters/lilly/lilly2.low/Walking Turn 180.glb', {
      lockFacing: true
    }),
    attack_horizontal: createAttackAnimation('/assets/characters/lilly/lilly2.low/Standing 1H Magic Attack 01.glb', {
      applyRootMotion: true,
      startOffset: 0.3,
      speed: 2
    }),
    magic_beam: createAttackAnimation('/assets/characters/lilly/lilly2.low/Standing 2H Magic Attack 04.glb', {
      applyRootMotion: true,
      // Loop range for channeled animation: intro (0-30%), loop (30-70%), outro (70-100%)
      // Adjust these values based on the actual animation keyframes
      loopRange: { start: 0.3, end: 0.7 },
      startOffset: 0.2,
      speed: 4
    }),
    hit_react: createIdleAnimation('/assets/characters/lilly/lilly2.low/Head Hit.glb', {
      speed: 2,
      phases: {
          windup: { start: 0.0, end: 0.2 },
          active: { start: 0.2, end: 0.55 },
          recovery: { start: 0.55, end: 1.0 }
      }
    }),
    death: createIdleAnimation('/assets/characters/hotdog/Zombie Dying.glb',{
        speed: 4,
        applyRootMotion: true
    })
  },
  transitions: [
    { from: 'idle', to: 'turn', duration: 0.1 },
    { from: 'run', to: 'turn', duration: 0.15 },
    { from: 'turn', to: 'run', duration: 0.2 },
    { from: 'turn', to: 'idle', duration: 0.15 },
    { from: 'land', to: 'run', duration: 0.15, blendRatio: 0.3 },
    { from: 'land', to: 'idle', duration: 0.2 },
    { from: 'run', to: 'idle', duration: 0.2 },
    { from: 'idle', to: 'run', duration: 0.3 },
    { from: 'idle', to: 'attack_horizontal', duration: 0.1 },
    { from: 'run', to: 'attack_horizontal', duration: 0.1 },
    { from: 'attack_horizontal', to: 'idle', duration: 0.2 }
  ],
  combatBindings: {
    action: 'projectile_throw',
    action2: 'magic_beam', // Ice beam on action2
    action3: 'beam_throw' // Beam projectile with video material (configured in Blender)
  },
  combatAttacks: {
    // Beam projectile attack - video material is auto-detected from mesh hm_materialVideo_* extras in Blender
    beam_throw: createRangedProjectileAttack({
      cooldown: 0,
      interruptionPolicy: { allowDamageInterrupt: true, allowPlayerCancel: false },
      impactFX: fromFXPreset('magic_hit'),
      //maskBones: [...UPPER_BODY_MASK], // Only affect upper body when moving
      applyMaskWhenInStates: ['run', 'walk'], // Apply mask when in these locomotion states
      speedWhenMasked: 1.5, // Faster animation when combined with running
      speedWhenUnmasked: 1.0, // Normal speed when idle/full body
      projectile: {
        assetId: 'beam_projectile',
        assetPath: '#beam', // Embedded entity reference - # prefix is REQUIRED!
        scale: 1.0,
        type: 'kinematic',
        speed: 25,
        directionMode: 'facing',
        maxLifetime: 5,
        muzzleBone: 'mixamorig:RightHand',
        // Light follows the projectile
        light: {
          color: new Color3(0.8, 0.4, 1.0), // Purple
          intensity: 3.0,
          range: 5.0,
        },
        // Particles on impact
        impactParticlePreset: 'magic_burst',
      }
    }),
    projectile_throw: createRangedProjectileAttack({
      cooldown: 0,
      interruptionPolicy: { allowDamageInterrupt: true, allowPlayerCancel: false },
      impactFX: fromFXPreset('ice_impact'),
      maskBones: [...UPPER_BODY_MASK], // Only affect upper body when moving
      applyMaskWhenInStates: ['run', 'walk'], // Apply mask when in these locomotion states
      speedWhenMasked: 1.5, // Faster animation when combined with running
      speedWhenUnmasked: 0.5, // Normal speed when idle/full body
      
      projectile: {

        assetId: 'cone_projectile',
        assetPath: '#cone-projectile', // Embedded entity reference - # prefix is REQUIRED!
        scale: 1.0,
        type: 'kinematic',
        speed: 30,
        directionMode: 'facing',
        hitRadius: 0.5, // Sphere sweep radius for more forgiving hit detection
        maxLifetime: 5,
        muzzleBone: 'mixamorig:RightHand',
        // Light follows the projectile
        light: {
          color: new Color3(0.3, 0.6, 1.0), // Ice blue
          intensity: 20.0,
          range: 10.0,
        },
        // Particles on impact
        impactParticlePreset: 'ice_burst',
      }
    }),
    magic_beam: createChanneledAttack({
      id: 'magic_beam_ice',
      damage: 5,
      channeled: {
        loopAnimation: 'magic_beam',
        endAnimation: 'attack_horizontal',
        tickInterval: 0.02, // 20 ticks per second for smooth particles
        lockFacing: false, // Allow turning during beam
        lockMovement: true, // Cannot run during beam attack
        minDuration: 0.5, // Must channel at least 0.5s before early exit
        maxDuration: 5.0, // 5 second max channel
        
        // Combined presets: particles + projectiles with timing control
        presets: combinePresets(
          // Muzzle light - flashing ice-blue to white
          createMuzzleLightPreset({
            boneName: 'mixamorig:RightHand',
            intensity: 5.0,
            range: 8.0,
            offset: new Vector3(0, 0, 0.3),
            colors: [
              { color: new Color3(0.5, 0.8, 1.0), probability: 0.1, intensity: 6 },  // Light ice blue
              { color: new Color3(0.6, 0.85, 1.0), probability: 0.2,intensity: 2 }, // Lighter blue
              { color: new Color3(0.8, 0.9, 1.0), probability: 0.3, intensity: 2 },  // Near white-blue
              { color: new Color3(0.4, 0.3, 1.0), probability: 0.4, intensity: 3 },  // Pure white
              { color: new Color3(0.6, 0.5, 1.0), probability: 0.4, intensity: 10 },  // Pure white
            ],
            switchInterval: [0.001, 0.003], // 100-200ms random interval
          }),
          // Beam particles + projectile spawning with precise timing
          createBeamWithProjectilesPreset({
            particlePreset: 'magic_beam_ice',
            boneName: 'mixamorig:RightHand',
            particleStartTime: 0.0,  // Start particles immediately
            particleEndTime: 1.0,     // Until channel ends
            projectileRate: 5,        // 5 projectiles per second
            projectileStartTime: 0.0, // Start projectiles at 10% (slight delay)
            projectileEndTime: 0.95,  // Stop just before end
            startBurstPreset: 'ice_burst',
            endBurstPreset: 'ice_burst',
            particleScale: 1.0,
            directionMode: 'facing',
            jitter: {
              position: 0.1,
              direction: 5, // degrees
            },
            // Static FX - video billboard at spell origin (no movement/collision)
            staticFx: [
              {
                id: 'hand_glow',
                assetPath: '#beam',
                boneName: 'mixamorig:RightHand',
                scale: 0.8,
                orientationMode: 'facing', // Keep horizontal, aligned with character facing
                startTime: 0.0,  // Start with particles
                endTime: 1.0,    // End with channel
                up: new Vector3(0, 1, 0),
                forward: new Vector3(0, 0, 1),
                playbackRate: 5
              },
            ],
          }),
          // Hit detection and damage
          createBeamDamagePreset({
            type: 'cone',
            range: 8,
            width: 15, // 15 degree cone half-angle
            baseDamage: 5,
            damageInterval: 0.25,
            damageCurve: 'rampUp',
          }),
        ),
      },
      // Projectile spawned by the preset (not every tick!)
      projectile: {
        assetId: 'beam',
        assetPath: '#beam', // Embedded entity reference - # prefix is REQUIRED!
        scale: 0.6, // Smaller projectiles for beam attack
        type: 'kinematic',
        speed: 20,
        directionMode: 'facing', // Follow character's facing direction
        maxLifetime: 2,
        muzzleBone: 'mixamorig:RightHand',
        // No projectile lights - muzzle light preset handles the bone-attached light
      },
      interruptionPolicy: { 
        allowDamageInterrupt: true, 
        allowPlayerCancel: true // Allow releasing button to cancel
      },
      cooldown: 0.0,
      globalCooldown: 0.25,
      
      // // Legacy hook still works alongside presets
      // onChannelTick: (ctx) => {
      //   // Log every 10 ticks for debugging
      //   if (ctx.tickCount % 10 === 0) {
      //     console.log(`[Ice Beam] Tick ${ctx.tickCount}, duration: ${ctx.channelDuration.toFixed(2)}s`);
      //   }
      // },
    }),
    
    // Fire beam variant - higher damage, shorter range
    magic_beam_fire: createChanneledAttack({
      id: 'magic_beam_fire',
      damage: 8,
      channeled: {
        loopAnimation: 'attack_horizontal',
        endAnimation: 'attack_horizontal',
        tickInterval: 0.08, // Faster ticks
        lockFacing: false, // Allow turning during beam
        lockMovement: true, // Cannot run during beam attack
        minDuration: 0.3, // Must channel at least 0.3s before early exit
        maxDuration: 3.0, // Shorter max duration
        
        // Custom preset stack for fire beam
        presets: combinePresets(
          // Particle stream with fire theme
          createParticleStreamPreset({
            preset: 'magic_beam_fire',
            boneName: 'mixamorig:RightHand',
            directionMode: 'facing',
          }),
          // Damage with burst curve (high initial damage)
          createBeamDamagePreset({
            type: 'cone',
            range: 6,
            width: 20, // Wider cone
            baseDamage: 8,
            damageInterval: 0.2,
            damageCurve: 'burst',
          }),
          // Visual effects on start/end
          createChannelEffectsPreset({
            startImpact: 'small',
            endImpact: 'medium',
            startBurst: { preset: 'fire_burst', scale: 1.5 },
            endBurst: { preset: 'fire_explosion', scale: 2.0 },
          }),
        ),
      },
      interruptionPolicy: { 
        allowDamageInterrupt: true, 
        allowPlayerCancel: true
      },
      cooldown: 3.0,
      globalCooldown: 0.5,
    })
  },
  submeshes: [
    // {
    //   id: 'weapon',
    //   source: '/assets/characters/daniel3/Bass.glb',
    //   attachBone: 'mixamorig:RightHand',
    //   scale: 1.0
    // }
  ],
  lifecycle: createBasicLifecycleScript({
    id: 'lilly-default-lifecycle',
    hitAnimation: 'hit_react',
    deathAnimation: 'death',
    despawnMode: 'after-animation'
  }),
  onHitReaction: ({ baseDamage }) => (baseDamage < 12 ? 'attack_horizontal' : undefined)
});